import { KeyboardEventCode } from '@lemonade-hq/cdk';
import type { DatePickerAria } from '@react-aria/datepicker';
import { clsx } from 'clsx';
import React, { forwardRef, useCallback } from 'react';
import { mergeRefs } from 'react-merge-refs';
import { useViewportSize } from '../../../hooks/useViewportSize';
import { spacing } from '../../../styles/spacing.css';
import { baseInput } from '../../BaseComponents.css';
import { Flex } from '../../Flex/Flex';
import type { IconName } from '../../Icon/Icon';
import { Icon } from '../../Icon/Icon';
import { DateField } from './DateField';
import { RightIcon } from './DateFieldRightIcon';
import * as styles from './DateFieldWrapper.css';

interface DateFieldWrapperProps extends React.ComponentProps<'div'> {
  readonly errorElement?: React.ReactNode;
  readonly label?: string;
  readonly onOpenChange: (isOpen: boolean) => void;
  readonly handleClear: () => void;
  readonly handleSegmentClick: (event: React.MouseEvent<HTMLParagraphElement>) => void;
  readonly handleSegmentKeyPress: (e: React.KeyboardEvent<HTMLParagraphElement>) => void;
  readonly isCalendarOpen: boolean;
  readonly fieldProps: React.ComponentProps<typeof DateField>;
  readonly showDateContent: boolean;
  readonly setIsBlurred: (isBlurred: boolean) => void;
  readonly groupProps: DatePickerAria['groupProps'];
  readonly disabled?: boolean;
  readonly discardable?: boolean;
  readonly hasError?: boolean;
  readonly icon?: IconName;
  readonly showDatePicker?: () => void;
  readonly keyboardOnly?: boolean;
  readonly isKeyboard?: boolean;
  readonly isBlurred?: boolean;
  readonly calendarRef?: React.RefObject<HTMLDivElement>;
}

export const DateFieldWrapper = forwardRef<HTMLDivElement, DateFieldWrapperProps>(
  (
    {
      children: _children,
      label,
      isCalendarOpen,
      onOpenChange,
      handleClear,
      handleSegmentClick,
      handleSegmentKeyPress,
      className,
      fieldProps,
      showDateContent,
      setIsBlurred,
      isBlurred,
      groupProps,
      disabled,
      discardable,
      hasError,
      icon,
      showDatePicker,
      keyboardOnly,
      isKeyboard,
      calendarRef,
      ...props
    },
    ref,
  ) => {
    const { isAboveDesktopViewport } = useViewportSize();
    const showCloseIcon =
      (keyboardOnly ?? isAboveDesktopViewport) && fieldProps.dateValue != null && Boolean(discardable);
    const showCalendarIcon = !keyboardOnly && (isKeyboard ?? !isAboveDesktopViewport);

    const dateFieldRef = React.useRef<HTMLDivElement>(null);
    const onKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLButtonElement>): void => {
        if ((e.key as KeyboardEventCode) === KeyboardEventCode.Enter) {
          onOpenChange(!isCalendarOpen);
          return;
        }

        groupProps.onKeyDown?.(e);
      },
      [onOpenChange, isCalendarOpen, groupProps],
    );

    const onClick = useCallback(
      (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        setIsBlurred(false);
        props.onClick?.(e);
        groupProps.onClick?.(e);
      },
      [groupProps, props, setIsBlurred],
    );

    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
    const showRightIcon = showDateContent && !disabled && (showCloseIcon || showCalendarIcon);

    const onBlur = useCallback(
      (e: React.FocusEvent<HTMLDivElement>) => {
        const dateFieldFieldContainsElement = dateFieldRef.current?.contains(e.relatedTarget);
        const calendarViewContainsElement = calendarRef?.current?.contains(e.relatedTarget);
        setIsBlurred(!calendarViewContainsElement && !dateFieldFieldContainsElement);
      },
      [calendarRef, dateFieldRef, setIsBlurred],
    );

    return (
      <Flex
        {...props}
        alignItems="center"
        aria-disabled={disabled}
        aria-expanded={isCalendarOpen}
        aria-label="date-picker"
        className={clsx(baseInput({ disabled: disabled }), styles.dateFieldWrapper, className)}
        cursor="pointer"
        gap={spacing.s12}
        justifyContent="space-between"
        onBlur={onBlur}
        onClick={onClick}
        onFocusCapture={() => setIsBlurred(false)}
        onKeyDown={onKeyDown}
        paddingBlock="0"
        paddingInlineEnd={spacing.s08}
        paddingInlineStart={icon != null ? spacing.s12 : spacing.s20}
        ref={mergeRefs([ref, dateFieldRef])}
        role="combobox"
        tabIndex={disabled ? -1 : 0}
      >
        {icon != null && (
          <Icon className={styles.presentationalIcon} name={icon} variant={disabled ? 'disabled' : 'secondary'} />
        )}
        <DateField
          disabled={disabled}
          hasError={hasError}
          isBlurred={isBlurred}
          onSegmentClick={handleSegmentClick}
          onSegmentKeyPress={handleSegmentKeyPress}
          placeholder={label}
          showDateContent={showDateContent}
          {...fieldProps}
        />
        {showRightIcon && (
          <RightIcon
            dateValue={fieldProps.dateValue}
            handleClear={handleClear}
            isCloseIcon={showCloseIcon}
            isKeyboard={isKeyboard}
            keyboardOnly={keyboardOnly}
            showDatePicker={showDatePicker}
          />
        )}
      </Flex>
    );
  },
);
